class MapState {
  constructor () {
    this.darkMode = false
    this.reset()
  }

  reset () {
    this.gridSpacing = 50 // 50px x 50px spacing
    this.gridDash = []
    this.gridLineWidth = 0.1

    this.mapImage = null
    this.origMap = null
    this.mapWorld = { x: 0, y: 0 } // world coords of the top-left corner of the map image
    this.canEditMap = false

    this.zoomValue = 1 // 100%

    this.cropRect = { x: 0, y: 0, w: 0, h: 0 }
    this.drag = null // { mode, startWorld:{x,y}, startRect:{...} }

    this.history = []
    this.historyIndex = -1

    this.panMode = false
    this.cropMode = false
    this.tracerMode = false // 'curve', 'line', 'edge'
    this.edgeDistanceMode = false
    this.pointDistanceMode = false
    this.addSensorMode = false
    this.groupMode = false
    this.dragMode = false
    this.swapMode = false
    this.linearFlowMode = false

    this.tracerPointsWorld = [] // for path tracer tool
    this.tracerPathWorld = [] // for path tracer tool
    this.lenPath = null // for path tracer tool
    this.lenUnit = null // for path tracer tool
    this.calibration = false

    this.cursorWorld = null
    this.boundaryInfo = null

    this.segments = []
    this.startPoint = null

    this.previewSensor = null
    this.selectedSensorId = null
    this.sensors = []
    this.nextSensorId = 1
    this.labelsOccupied = []
    this.zoneDrag = null
    this.zoneHitTolerancePx = 10
    this.sensorNameChangedOnce = false

    this.flowStartSensorId = null
    this.flowPreviewWorld = null

    this.groupSensorIds = []
    this.groups = []
    this.nextGroupId = 1

    this.visibleSensors = true
    this.visiblePointDistances = true
    this.visibleLinearFlows = true
    this.visibleLables = true
  }

  getSensorById (id) {
    return this.sensors.find(sensor => String(sensor.id) === String(id))
  }

  getGroupById (groupId) {
    return this.groups.find(group => String(group.id) === String(groupId))
  }

  uniqueIds (ids = []) {
    const out = []
    ids.forEach(id => {
      const numericId = Number(id)
      if (!out.includes(numericId)) out.push(numericId)
    })
    return out
  }

  normalizeGroupState () {
    this.groupSensorIds = this.uniqueIds(this.groupSensorIds)
      .filter(sensorId => !!this.getSensorById(sensorId))

    this.groups = this.groups
      .map(group => ({
        ...group,
        id: Number(group.id),
        name: group.name ?? '',
        sensorIds: this.uniqueIds(group.sensorIds)
          .filter(sensorId => !!this.getSensorById(sensorId))
      }))
      .filter(group => group.sensorIds.length > 0)

    this.groups.forEach(group => {
      if (!Number.isFinite(group.id)) {
        group.id = this.nextGroupId++
      }
    })
  }

  hasLooseSensorScope (sensorId) {
    return this.groupSensorIds.includes(Number(sensorId))
  }

  addLooseSensorScope (sensorId) {
    const numericId = Number(sensorId)
    if (!this.getSensorById(numericId)) return false
    if (this.groupSensorIds.includes(numericId)) return false
    this.groupSensorIds.push(numericId)
    return true
  }

  removeLooseSensorScope (sensorId) {
    const numericId = Number(sensorId)
    const before = this.groupSensorIds.length
    this.groupSensorIds = this.groupSensorIds.filter(id => id !== numericId)
    return before !== this.groupSensorIds.length
  }

  createGroup (sensorIds = [], name = '') {
    const uniqueSensorIds = this.uniqueIds(sensorIds).filter(sensorId => !!this.getSensorById(sensorId))
    if (uniqueSensorIds.length === 0) return null

    const group = {
      id: this.nextGroupId++,
      name,
      sensorIds: uniqueSensorIds
    }

    this.groups.push(group)
    return group
  }

  addSensorToGroup (groupId, sensorId) {
    const group = this.getGroupById(groupId)
    const numericId = Number(sensorId)

    if (!group || !this.getSensorById(numericId)) return false
    if (group.sensorIds.includes(numericId)) return false

    this.removeLooseSensorScope(numericId)

    group.sensorIds.push(numericId)
    return true
  }

  convertLooseSensorToGroup (looseSensorId, incomingSensorId) {
    const looseId = Number(looseSensorId)
    const incomingId = Number(incomingSensorId)

    if (!this.hasLooseSensorScope(looseId)) return null
    if (!this.getSensorById(incomingId)) return null
    if (looseId === incomingId) return null

    this.removeLooseSensorScope(looseId)
    this.removeLooseSensorScope(incomingId)

    return this.createGroup([looseId, incomingId])
  }

  removeSensorFromGroup (groupId, sensorId) {
    const group = this.getGroupById(groupId)
    const numericId = Number(sensorId)
    if (!group) return false

    const before = group.sensorIds.length
    group.sensorIds = group.sensorIds.filter(id => id !== numericId)
    if (before === group.sensorIds.length) return false

    if (group.sensorIds.length === 1) {
      this.addLooseSensorScope(group.sensorIds[0])
      this.groups = this.groups.filter(entry => entry.id !== group.id)
    } else if (group.sensorIds.length === 0) {
      this.groups = this.groups.filter(entry => entry.id !== group.id)
    }

    return true
  }

  removeSensorEverywhere (sensorId) {
    const numericId = Number(sensorId)
    this.removeLooseSensorScope(numericId)

    this.groups = this.groups
      .map(group => ({
        ...group,
        sensorIds: group.sensorIds.filter(id => id !== numericId)
      }))
      .filter(group => group.sensorIds.length > 0)

    const collapsedGroups = []
    this.groups = this.groups.filter(group => {
      if (group.sensorIds.length === 1) {
        collapsedGroups.push(group.sensorIds[0])
        return false
      }
      return true
    })

    collapsedGroups.forEach(remainingSensorId => this.addLooseSensorScope(remainingSensorId))
  }
}

export const state = new MapState()
